// src/components/MetaCampaigns/MetaCampaigns.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon
} from '@mui/icons-material';

const MetaCampaigns = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeStep, setActiveStep] = useState(0);

  // Estados do formulário
  const [formData, setFormData] = useState({
    name: '',
    objective: 'OUTCOME_AWARENESS',
    dailyBudget: '',
    audience: {
      ageMin: 18,
      ageMax: 65,
      genders: [1, 2],
      interests: [],
      behaviors: []
    },
    adText: {
      headline: '',
      body: '',
      description: ''
    },
    websiteUrl: ''
  });

  const steps = ['Informações Básicas', 'Público-Alvo', 'Conteúdo do Anúncio'];

  const objectives = [
    { value: 'OUTCOME_AWARENESS', label: 'Reconhecimento da Marca' },
    { value: 'OUTCOME_ENGAGEMENT', label: 'Engajamento' },
    { value: 'OUTCOME_LEADS', label: 'Geração de Leads' },
    { value: 'OUTCOME_SALES', label: 'Vendas' },
    { value: 'OUTCOME_TRAFFIC', label: 'Tráfego' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAudienceChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      audience: {
        ...prev.audience,
        [field]: value
      }
    }));
  };

  const handleAdTextChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      adText: {
        ...prev.adText,
        [field]: value
      }
    }));
  };

  const handleNext = () => {
    // Validações por etapa
    if (activeStep === 0) {
      if (!formData.name || !formData.dailyBudget) {
        setError('Nome e orçamento são obrigatórios');
        return;
      }
      if (formData.dailyBudget < 20) {
        setError('Orçamento mínimo é R$ 20,00 por dia');
        return;
      }
    }

    if (activeStep === 1) {
      if (formData.audience.ageMin < 18 || formData.audience.ageMax > 65) {
        setError('Idade deve estar entre 18 e 65 anos');
        return;
      }
    }

    if (activeStep === 2) {
      if (!formData.adText.headline || !formData.adText.body) {
        setError('Título e texto do anúncio são obrigatórios');
        return;
      }
    }

    setError('');
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/meta/campaigns`,
        {
          name: formData.name,
          objective: formData.objective,
          dailyBudget: parseFloat(formData.dailyBudget) * 100, // Converter para centavos
          audience: formData.audience,
          adText: formData.adText,
          websiteUrl: formData.websiteUrl
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setSuccess('Campanha criada com sucesso!');
        setTimeout(() => {
          navigate('/unified-dashboard');
        }, 2000);
      }
    } catch (err) {
      console.error('Erro ao criar campanha:', err);
      setError(err.response?.data?.error || 'Erro ao criar campanha');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome da Campanha"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Ex: Campanha Verão 2024"
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Objetivo da Campanha</InputLabel>
                <Select
                  value={formData.objective}
                  onChange={(e) => handleChange('objective', e.target.value)}
                  label="Objetivo da Campanha"
                >
                  {objectives.map(obj => (
                    <MenuItem key={obj.value} value={obj.value}>
                      {obj.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Orçamento Diário"
                type="number"
                value={formData.dailyBudget}
                onChange={(e) => handleChange('dailyBudget', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
                helperText="Mínimo: R$ 20,00 por dia"
                required
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Defina seu Público-Alvo
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Idade Mínima"
                type="number"
                value={formData.audience.ageMin}
                onChange={(e) => handleAudienceChange('ageMin', parseInt(e.target.value))}
                inputProps={{ min: 18, max: 65 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Idade Máxima"
                type="number"
                value={formData.audience.ageMax}
                onChange={(e) => handleAudienceChange('ageMax', parseInt(e.target.value))}
                inputProps={{ min: 18, max: 65 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Gênero</InputLabel>
                <Select
                  multiple
                  value={formData.audience.genders}
                  onChange={(e) => handleAudienceChange('genders', e.target.value)}
                  label="Gênero"
                >
                  <MenuItem value={1}>Masculino</MenuItem>
                  <MenuItem value={2}>Feminino</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Alert severity="info">
                Por padrão, sua campanha será segmentada para todo o Brasil. 
                Você poderá refinar a segmentação depois de criar a campanha.
              </Alert>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Conteúdo do Anúncio
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Título do Anúncio"
                value={formData.adText.headline}
                onChange={(e) => handleAdTextChange('headline', e.target.value)}
                placeholder="Ex: Aproveite 50% de desconto!"
                helperText="Máximo 40 caracteres"
                inputProps={{ maxLength: 40 }}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Texto Principal"
                value={formData.adText.body}
                onChange={(e) => handleAdTextChange('body', e.target.value)}
                placeholder="Descreva sua oferta de forma atrativa..."
                helperText="Máximo 125 caracteres"
                inputProps={{ maxLength: 125 }}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                value={formData.adText.description}
                onChange={(e) => handleAdTextChange('description', e.target.value)}
                placeholder="Informações adicionais..."
                helperText="Opcional - Máximo 30 caracteres"
                inputProps={{ maxLength: 30 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="URL do Site"
                value={formData.websiteUrl}
                onChange={(e) => handleChange('websiteUrl', e.target.value)}
                placeholder="https://seusite.com.br"
                helperText="Para onde o anúncio irá direcionar"
              />
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
      <Box sx={{ maxWidth: 900, mx: 'auto', px: 2 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/unified-dashboard')}
            sx={{ mb: 2 }}
          >
            Voltar ao Dashboard
          </Button>
          
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CampaignIcon sx={{ fontSize: 40, color: '#1877f2' }} />
              <Box>
                <Typography variant="h4" component="h1">
                  Nova Campanha Meta Ads
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Crie sua campanha em 3 passos simples
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Stepper */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Mensagens */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Conteúdo do Step */}
          <Box sx={{ mb: 4 }}>
            {renderStepContent(activeStep)}
          </Box>

          {/* Botões de Navegação */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              disabled={activeStep === 0 || loading}
              onClick={handleBack}
            >
              Voltar
            </Button>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    bgcolor: '#1877f2',
                    '&:hover': { bgcolor: '#1565c0' }
                  }}
                >
                  {loading ? 'Criando...' : 'Criar Campanha'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  sx={{
                    bgcolor: '#1877f2',
                    '&:hover': { bgcolor: '#1565c0' }
                  }}
                >
                  Próximo
                </Button>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Informações Adicionais */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            ℹ️ Informações Importantes
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            • Sua campanha será criada como <strong>rascunho</strong> inicialmente
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            • Você poderá ativá-la depois de revisar todas as configurações
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            • Para publicar no Meta Ads, você precisará conectar sua conta do Facebook Business
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • O orçamento mínimo é de R$ 20,00 por dia
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default MetaCampaigns;